import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { getBreadcrumbs } from './components/HeadlineBreadcrumb/breadcrumbs.js';
import { HeadlineBreadcrumb } from './components/HeadlineBreadcrumb';
import HeadlineTimestamp from './components/HeadlineTimestamp/index.jsx';
import HeadlineByline from './components/HeadlineByline/index.jsx';
import HeadlineComments from './components/HeadlineComments';
import { checkAdDisclaimer } from './components/HeadlineByline/bylines.js';
import HeadlineBadges, { Badge } from './components/HeadlineBadges/index.jsx';
import styles from './headline.module.scss';
import { withTranslation } from '../../i18n';
import { withErrorBoundary } from '@buzzfeed/react-components';
import { captureException } from '@sentry/nextjs';
import DestinationContext from '../../contexts/destination';
import { isNews } from '../../utils/isNews.js';
import CommerceDisclaimer from '../CommerceDisclaimer';
import { useUnitTracking } from '../../hooks/analytics/useUnitTracking';

function QuizBreadcrumbLogo({ buzzId }) {
  const trackingData = React.useMemo(
    () => ({
      eventCategory: 'Header', eventAction: 'link', eventLabel: '/quizzes',
      unit_type: 'buzz_head', unit_name: buzzId, item_name: 'quizzes', item_type: 'text',
      position_in_unit: 0, target_content_type: 'feed', target_content_id: 'quizzes',
    }),
    [buzzId]
  );
  const unitEl = useUnitTracking(trackingData);

  return (
    <a
      ref={unitEl}
      className={styles.quizBreadcrumbLogo}
      href="/quizzes"
    >
      <span className={styles.screenReaderOnly}>Quizzes</span>
      <svg xmlns="http://www.w3.org/2000/svg" width="120" height="22" viewBox="0 0 120 22" fill="none" aria-hidden="true">
        <circle cx="9.14643" cy="10.3168" r="9.14643" fill="#FFED00" />
        <path d="M112.954 19.4677C110.305 19.4677 106.994 18.5936 105.405 17.3488L107.259 14.4353C108.663 15.4153 110.967 16.2099 113.166 16.2099C114.755 16.2099 115.179 15.8391 115.179 15.3888C115.179 14.7266 113.907 14.5942 112.345 14.4088C109.617 14.1439 105.909 13.7466 105.909 10.3564C105.909 7.78722 108.478 6.03912 112.53 6.03912C115.205 6.03912 117.88 6.83371 119.443 7.86668L117.801 10.6213C116.556 9.7737 114.437 9.16451 112.557 9.16451C111.232 9.16451 110.756 9.42937 110.756 9.9591C110.756 10.4888 111.894 10.6213 113.351 10.7802C116.079 11.0715 119.999 11.5218 119.999 15.0974C119.999 17.7461 117.589 19.4677 112.954 19.4677Z" fill="black" />
        <path d="M97.8001 19.4677C93.2179 19.4677 89.2715 17.4547 89.2715 12.7402C89.2715 8.55533 92.7677 6.03912 97.4558 6.03912C101.958 6.03912 105.269 8.74073 105.269 13.4023V14.1439H94.5423C95.0455 15.3623 96.2904 16.051 98.5417 16.051C100.025 16.051 101.535 15.6272 102.435 15.1239L104.051 17.9315C102.541 18.885 100.502 19.4677 97.8001 19.4677ZM94.4628 11.2569H100.343C100.051 10.224 99.1774 9.34992 97.4293 9.34992C95.8666 9.34992 94.8336 10.0121 94.4628 11.2569Z" fill="black" />
        <path d="M74.5679 19.1496V15.521L81.4278 10.3297H74.5679V6.35675H88.3408V9.85295L81.4013 15.1502H88.4467V19.1496H74.5679Z" fill="black" />
        <path d="M59.4663 19.1496V15.521L66.3263 10.3297H59.4663V6.35675H73.2392V9.85295L66.2998 15.1502H73.3451V19.1496H59.4663Z" fill="black" />
        <path d="M53.0136 19.1499V6.35703H58.0725V19.1499H53.0136ZM52.6958 2.49002C52.6958 1.05976 53.8877 0.000305176 55.5298 0.000305176C57.172 0.000305176 58.3904 1.05976 58.3904 2.49002C58.3904 3.92028 57.172 4.97974 55.5298 4.97974C53.8877 4.97974 52.6958 3.92028 52.6958 2.49002Z" fill="black" />
        <path d="M41.4759 19.4675C38.4564 19.4675 36.417 17.7459 36.417 13.9583V6.35675H41.5024V13.2962C41.5024 14.7264 42.244 15.3091 43.7272 15.3091C44.8396 15.3091 45.9256 14.7794 46.5613 14.1702V6.35675H51.6201V19.1496H46.5613V17.2426C45.4223 18.3815 43.5948 19.4675 41.4759 19.4675Z" fill="black" />
        <path d="M22.7152 12.8775L26.8735 10.8116L28.3038 13.5662C29.0984 12.7451 29.5486 11.6327 29.5486 10.3084C29.5486 7.31542 27.2973 5.48786 23.9071 5.48786C20.5168 5.48786 18.292 7.31542 18.292 10.3084C18.292 13.3013 20.5168 15.1289 23.9071 15.1289L22.7152 12.8775ZM12.4385 10.3084C12.4385 4.32247 17.5768 1.17059 23.9071 1.17059C30.2373 1.17059 35.3756 4.32247 35.3756 10.3084C35.3756 13.937 33.4951 16.5062 30.6081 17.9894L31.5351 19.7905L27.4033 21.8299L26.0789 19.3137C25.3903 19.3932 24.6487 19.4462 23.9071 19.4462C17.5768 19.4462 12.4385 16.2943 12.4385 10.3084Z" fill="black" />
      </svg>
    </a>
  );
}

export function Headline({
  designUpdateEnabled = false,
  buzz,
  badges,
  isGames = false,
  isQuiz = false,
  shoppingImage = null,
  t,
}) {
  const { destination } = useContext(DestinationContext);
  const { isCommentsPage } = buzz;
  const timestampPublished = Number(buzz.published);
  const timestampUpdated =
    'edit_updated' in buzz ? Number(buzz.edit_updated) : 0;
  const showUpdatedTimestampOnly = buzz.tags.includes(
    '--updated-timestamp-only'
  );

  const isAd = buzz.flags.ad === 1;
  const countryCode = buzz.country_code;
  const { primary, secondary } = getBreadcrumbs(buzz, badges);
  const hasAdDisclaimer =
    checkAdDisclaimer(buzz.bylines) && buzz.classification.edition !== 'Japan';

  // If it's not BFN post, the longform custom header will override the title and description set on the post.
  const { title, description } = buzz.longform_custom_header && !isNews(buzz)
    ? buzz.longform_custom_header
    : buzz;

  const showBreadcrumb = !isNews(buzz) || (isNews(buzz) && (primary.length || secondary.length) > 0);
  const showQuizBreadcrumbRow = designUpdateEnabled && isQuiz && !isAd;
  const hasAdditionalBreadcrumbs = primary.length > 0 || secondary.length > 0;

  // The longform custom header will display the <h1> overlaid on the image if configured with `text_over`
  // We use CSS to visually hide the title but keep it in the DOM as an a11y landmark
  // Visual title will be added in FeaturedImage/index.jsx
  const hasImageText =
    buzz.longform_custom_header &&
    buzz.longform_custom_header.style &&
    buzz.longform_custom_header.style.text_over;

  if (buzz.hasQuizSharedResult) {
    const quizBadge = badges[0];
    quizBadge.has_svg = true;
    quizBadge.badge_language = 'general';

    return (
      <div className={styles.sharedResultHeadlineWrapper}>
        <div className={styles.sharedResultBadge}>
          <Badge index={0} badge={quizBadge} buzzId={buzz.buzzId} />
          <p>Result from</p>
        </div>
        <h1 className={styles.sharedResultTitle}>
          <a href={buzz.canonical_url}>{buzz.title}</a>
        </h1>
      </div>
    );
  }

  const breadcrumb = (
    <div className={`${styles.container} embed-breadcrumbs`}>
    {isAd ? (
      <span className={styles.paidPostBreadcrumb}>{t('paid_post')}</span>
    ) : (
      <nav
        aria-label="Breadcrumb"
        className={styles.breadcrumbContainer}
      >
        <HeadlineBreadcrumb
          breadcrumbs={primary}
          secondaryBreadcrumbs={secondary}
          designUpdateEnabled={designUpdateEnabled}
        />
        {destination === 'buzzfeed' && !designUpdateEnabled &&
          primary.length !== 0 &&
          timestampPublished > 0 && (
            <span className={`bold ${styles.middot}`}>&#183;</span>
          )}
      </nav>
    )}
    {destination === 'buzzfeed' && !designUpdateEnabled && (
      <HeadlineTimestamp
        timestampPublishedUnix={timestampPublished}
        timestampUpdatedUnix={timestampUpdated}
        countryCode={countryCode}
        destination={destination}
      />
    )}
  </div>);

  const quizBreadcrumbRow = showQuizBreadcrumbRow && (
    <div className={`${styles.container} ${styles.postHeadBadges} ${styles.quizBreadcrumbRow}`}>
      <nav aria-label="Breadcrumb" className={styles.quizBreadcrumb}>
        <QuizBreadcrumbLogo buzzId={buzz.id} />
        {hasAdditionalBreadcrumbs && (
          <span aria-hidden="true" className={styles.quizBreadcrumbSeparator}>
            &#183;
          </span>
        )}
        <HeadlineBreadcrumb
          breadcrumbs={primary}
          secondaryBreadcrumbs={secondary}
          useQuizBreadcrumbStyle={showQuizBreadcrumbRow}
        />
      </nav>
      <div className={styles.quizBadges}>
        <HeadlineBadges
          badges={badges}
          buzzId={buzz.id}
          countryCode={countryCode}
          designUpdateEnabled={designUpdateEnabled}
          flags={buzz.flags}
          isGames={isGames}
        />
      </div>
    </div>
  );

  const shouldDisplay = {
    adDisclaimer: !isCommentsPage && hasAdDisclaimer,
    badges: !isCommentsPage && !buzz.isAd && destination === 'buzzfeed',
    breadcrumbAboveTitle: !isCommentsPage && showBreadcrumb && !designUpdateEnabled,
    breadcrumbBelowTitle: isCommentsPage && showBreadcrumb && !designUpdateEnabled,
    byline: !isCommentsPage && !designUpdateEnabled,
    commerceDisclaimer: !isCommentsPage,
    communityDisclaimer: !isCommentsPage && !buzz.isModerated,
    timestamp: !isCommentsPage && destination === 'buzzfeed_news',
  };

  return (
    <div
      className={`${styles.headline} ${styles[buzz.bfpTheme] || ''} ${
        styles[destination]
      } ${
        designUpdateEnabled ? styles.postHead : ''
      } ${
        isGames ? styles.games : ''
      } ${
        isQuiz ? styles.quiz : ''
      } embed-buzz-header`}
    >
      {designUpdateEnabled && !showQuizBreadcrumbRow && breadcrumb}
      {shouldDisplay['commerceDisclaimer'] && !designUpdateEnabled && <CommerceDisclaimer buzz={buzz} />}
      {quizBreadcrumbRow}
      {shouldDisplay['badges'] && !showQuizBreadcrumbRow && (
        <div className={`${styles.container} ${designUpdateEnabled ? styles.postHeadBadges : ''}`}>
          <HeadlineBadges
            badges={badges}
            buzzId={buzz.id}
            countryCode={countryCode}
            designUpdateEnabled={designUpdateEnabled}
            flags={buzz.flags}
            isGames={isGames}
          />
        </div>
      )}
      {isCommentsPage && !designUpdateEnabled && <HeadlineComments />}
      {shouldDisplay['communityDisclaimer'] && (
        <div className={`${styles.disclaimer} ${styles.container}`}>
          {t('community_disclaimer')}
          <a className="blue-link" href="http://community.buzzfeed.com/">
            {' '}
            {t('community_try')}
          </a>
        </div>
      )}
      {shouldDisplay['adDisclaimer'] && (
        <div className={`${styles.disclaimer} ${styles.container}`}>
          {t('ad_disclaimer')}
        </div>
      )}
      {shouldDisplay['breadcrumbAboveTitle'] && breadcrumb}

      <div
        className={`${styles.container} ${
          hasImageText ? styles.hideTitle : ''
        } ${
          designUpdateEnabled ? styles.postHeadTitleContainer : ''
        } embed-headline`}
      >
        {shoppingImage && (
          <img
            src={shoppingImage}
            alt=""
            width={320}
            height={320}
            className={styles.shoppingImage}
            aria-hidden="true"
            loading="lazy"
          />
        )}
        <h1
          className={`${styles.title} ${buzz.isShopping ? styles.shoppingTitle : ''} embed-headline-title`}
          dangerouslySetInnerHTML={{ __html: title }}
        />
        <p
          className={`${styles.description} embed-headline-description`}
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </div>
      {shouldDisplay['breadcrumbBelowTitle'] && breadcrumb}
      {shouldDisplay['byline'] &&
        <HeadlineByline
          bylines={buzz.bylines}
          isAd={isAd}
          buzz={buzz}
        />}
      {shouldDisplay['timestamp'] && (
        <HeadlineTimestamp
          timestampPublishedUnix={timestampPublished}
          timestampUpdatedUnix={timestampUpdated}
          showUpdatedTimestampOnly={showUpdatedTimestampOnly}
          countryCode={countryCode}
          destination={destination}
        />
      )}
    </div>
  );
}

Headline.propTypes = {
  buzz: PropTypes.object.isRequired,
  badges: PropTypes.array,
  isGames: PropTypes.bool,
  isQuiz: PropTypes.bool,
  shoppingImage: PropTypes.string,
};

export default withErrorBoundary(withTranslation('common')(Headline), {
  onError: captureException,
});
